<template>
  <Card class="w-full lg:w-2/4">
    <template v-slot:card-header>
      <div id="logo" class="mt-3">
        <Logo
          :path="locationLogo || defaultLogo"
          width="160"
          className="flex justify-center"
          alt="SignUpLogo"
          ref="logoImage"
        />
      </div>
      <StepProgressBar
        v-if="!isFreeTrial"
        :steps="3"
        :stepSelected="1"
        class="mt-8"
      />
    </template>
    <template v-slot:card-body>
      <div v-if="isFreeTrial">
        <h5 class="text-center mt-4 mb-3 font-semibold text-xl">
          START YOUR FREE TRIAL
        </h5>
        <p class="text-gray-400 mb-3">
          Creating your account below starts you on your 30-day free trial period
          with {{ appName }}, giving you full access to the program during this
          time. After the trial, you can choose to subscribe and continue your
          training from where you left off.
        </p>
      </div>
      <h5 v-else class="text-center mt-8 font-semibold text-xl">
        WELCOME! LET'S SETUP YOUR PROFILE
      </h5>
      <form class="mt-8" @submit.prevent="handleSubmitRegister">
        <div v-if="isFreeTrial" class="form__div">
          <input
            class="form__input"
            type="text"
            placeholder=" "
            id="organizationCode"
            v-model="organizationCode"
            required
          />
          <label for="organizationCode" class="form__label">Organization Code</label>
        </div>
        <div class="form__div">
          <input
            class="form__input"
            type="text"
            placeholder=" "
            id="firstName"
            v-model="firstName"
            required
          />
          <label for="firstName" class="form__label">First name</label>
        </div>
        <div class="form__div">
          <input
            class="form__input"
            type="text"
            placeholder=" "
            id="lastName"
            v-model="lastName"
            required
          />
          <label for="lastName" class="form__label">Last name</label>
        </div>
        <div class="form__div">
          <input
            class="form__input"
            type="email"
            id="email"
            placeholder=" "
            v-model="email"
            required
          />
          <label for="email" class="form__label">Email</label>
        </div>
        <PhoneInput
          v-model="phoneNumber"
          @onValidation="handlePhoneValidation"
        />
        <div class="form__div">
          <input
            class="form__input"
            type="password"
            placeholder=" "
            id="password"
            v-model="password"
            @click="changeShowPasswordReq()"
            @focus="changeShowPasswordReq()"
            required
          />
          <label for="password" class="form__label">Password</label>
          <div
            v-if="showPasswordReq"
            class="absolute -left-72 -top-2/3 p-4 border-2 bg-white"
          >
            <span
              class="absolute top-1/4 w-4 h-4 transform -rotate-45 border-b-2 border-r-2 z-10 bg-white"
              style="left: 98%"
            ></span>
            <p>To make your password stronger:</p>
            <ol class="list-disc list-inside m-2">
              <li>Min 8 characters</li>
              <li>Min 1 number</li>
              <li>Min 1 capital letter</li>
              <li>Min 1 special character</li>
            </ol>
          </div>
        </div>
        <div class="form__div">
          <input
            class="form__input"
            type="password"
            placeholder=" "
            id="confirm-password"
            v-model="confirmPassword"
            required
          />
          <label for="confirm-password" class="form__label"
            >Confirm password</label
          >
        </div>
        <button
          id="b-signup"
          class="text-darkBlue w-full text-darkBlue p-3 border-0 bg-mindaro font-semibold"
          type="submit"
          :disabled="props.proccessingSignup"
        >
          <em v-if="props.proccessingSignup" class="fa fa-spinner fa-spin"></em>
          Sign Up
        </button>
      </form>
      <Modal
        :show="showModal"
        :cancel="true"
        cancelText="Continue"
        confirmationText="Login"
        :isConfirmDisabled="false"
        width="500px"
        @cancel="handleCancel"
        @confirm="handleConfirm"
      >
        <template v-slot:header>
          <h2 class="font-bold mb-4">Oops, this email is already registered</h2>
        </template>
      </Modal>
    </template>
    <template v-slot:card-footer>
      <div class="text-center text-color m-0">
        Already have an account? |
        <router-link :to="{ name: 'Login' }" class="text-color underline"
          >Login</router-link
        >
      </div>
    </template>
  </Card>
</template>

<script setup>
import { ref, onMounted, watch, computed } from "vue";
import { useToast } from "vue-toastification";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";

import Card from "@/components/auth/Card.vue";
import Logo from "@/components/auth/Logo.vue";
import StepProgressBar from "@/components/general/StepProgressBar.vue";
import Modal from "@/components/general/ConfirmationModal.vue";
import jwt_decode from "jwt-decode";
import PhoneInput from "@/components/general/PhoneInput.vue";
import { expressions } from "@/components/general/utils";

const router = useRouter();
const route = useRoute();
const toast = useToast();
const store = useStore();
const emit = defineEmits(["registerEvent", "update:showModal"]);

const props = defineProps({
  emailProp: {
    type: String,
    default: null,
  },
  error: {
    type: String,
    default: null,
  },
  proccessingSignup: {
    type: Boolean,
    default: false,
  },
  showModal: {
    type: Boolean,
    default: false,
  },
});

const phoneNumberValid = ref(false);
const phoneNumberError = ref("");
const firstName = ref(null);
const lastName = ref(null);
const email = ref(props.emailProp);
const phoneNumber = ref("");
const password = ref(null);
const passwordRegex = ref(
  /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[#?!@$ %^&*-.]).{8,}$/
);
const confirmPassword = ref(null);
const showPasswordReq = ref(false);
const isFreeTrial = ref(false);
const organizationCode = ref(null);
const timeZone = ref(null);
const locationName = ref(null);
const defaultLogo = ref(require("@/assets/brain-logo.png"));

const locationLogo = computed(() => store.getters["location/getLocationLogo"]);

const appName = computed(() => store.getters["location/getAppName"]);

onMounted(async () => {
  try {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    timeZone.value = userTimeZone;
  } catch (error) {
    timeZone.value = "UTC";
  }

  const { freeTrial, data } = route.query;

  if (freeTrial === "true") {
    isFreeTrial.value = true;
  } else if (data) {
    try {
      const decoded = jwt_decode(data);
      locationName.value = decoded.location_name;

      store.commit("location/setLocationName", decoded.location_name);
      store.commit("location/setLocationLogo", decoded.logo_url);
    } catch (error) {
      locationName.value = null;
    }
  }
});

function handleSubmitRegister() {
  if (validateForm()) {
    emitRegisterEvent();
  }
}

function validateForm() {
  if (!firstName.value?.trim()) {
    showError("First name cannot be empty or contain only spaces.");
    return false;
  }

  if (!lastName.value?.trim()) {
    showError("Last name cannot be empty or contain only spaces.");
    return false;
  }

  if (!phoneNumber.value) {
    showError("Phone number is required");
    return false;
  }

  if (!phoneNumberValid.value) {
    showError(phoneNumberError.value || "Please enter a valid phone number");
    return false;
  }

  if (!expressions.email.test(email.value)) {
    showError("Invalid email");
    return false;
  }

  if (isFreeTrial.value && !organizationCode.value?.trim()) {
    showError("Organization code is required for free trial");
    return false;
  }

  return validatePassword();
}

function validatePassword() {
  if (password.value !== confirmPassword.value) {
    showError("Passwords do not match.");
    return false;
  }

  if (password.value.length < 8) {
    showError(
      "This password is too short. It must contain at least 8 characters."
    );
    return false;
  }

  if (!passwordRegex.value.test(password.value)) {
    showError("Password does not satisfy the requirements.");
    return false;
  }
  return true;
}

function changeShowPasswordReq() {
  showPasswordReq.value = true;
}

function showError(errorMess) {
  toast.error(errorMess);
}

function emitRegisterEvent() {
  emit("registerEvent", {
    firstName: firstName.value,
    lastName: lastName.value,
    email: email.value,
    password: password.value,
    fromWeb: false,
    phoneNumber: phoneNumber.value,
    dependents: isFreeTrial.value ? 1 : 0,
    isFreeTrial: isFreeTrial.value,
    facilityCode: isFreeTrial.value ? organizationCode.value : null,
    locationName: locationName.value,
    timeZone: timeZone.value,
  });
}

function handleCancel() {
  emit("update:showModal", false);
}

function handleConfirm() {
  router.push({
    name: "Login",
    query: {
      email: email.value,
    },
  });
}

function handlePhoneValidation(isValid) {
  phoneNumberValid.value = isValid;
  phoneNumberError.value =
    !isValid && phoneNumber.value ? "Please enter a valid phone number" : "";
}

watch(password, (newPassword) => {
  const reg = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[#?!@$ %^&*-.]).{8,}$/;
  if (reg.test(newPassword)) {
    showPasswordReq.value = false;
  }
});
</script>

<style scoped>
#b-signup:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

#b-signup {
  background-color: #edfe86;
  border: none;
  padding: 11px;
  width: 100%;
  cursor: pointer;
}
</style>
